import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaSyncAlt } from "react-icons/fa";
import Dropdown from "../../Component/Common/Dropdown";
import { InputField } from "../../Component/Common/InputField";
import CustomPagination from "../../Component/Common/CustomPagination";
import ConfirmModal from "../../Component/Common/ConstomModal/ConfirmModal";
import { VoterTable } from "./components/VoterTable";
import { useVoterListFetch } from "./components/useVoterListFetch";
import { PER_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from "./components/voterHelpers";

const voterName = (voter) => voter.first_name_e || voter.first_name_h || voter.full_name_e || "";

// Search-first page sorted alphabetically by name. There is no working
// server-side ordering param (the `ordering` filter field exists but is
// never honoured by the backend - confirmed during inspection), so this
// sorts client-side over the same booth-scoped full fetch that already
// powers the stats bar (`statsList`), same as Age Wise Search - a booth
// must be selected before searching.
export const AlphabeticalSearch = () => {
  const navigate = useNavigate();
  const v = useVoterListFetch();

  const [startsWith, setStartsWith] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

  const nameFilteredSorted = useMemo(() => {
    const source = Array.isArray(v.statsList?.results) ? v.statsList.results : [];
    const prefix = startsWith.trim().toLowerCase();
    return source
      .filter((voter) => !prefix || voterName(voter).toLowerCase().startsWith(prefix))
      .sort((a, b) => voterName(a).localeCompare(voterName(b)));
  }, [v.statsList, startsWith]);

  const pageCount = Math.max(Math.ceil(nameFilteredSorted.length / limit), 0);
  const pageVoters = nameFilteredSorted.slice(page * limit, page * limit + limit);

  // Pass this page's path so the voter form closes back here (e.g. the
  // Dashboard), not always to Voter Search.
  const location = useLocation();
  const backState = { state: { from: location.pathname } };
  const editVoter = (item) => navigate(`/voter-management/add?editId=${item.id}`, backState);
  const viewVoter = (item) => navigate(`/voter-management/add?editId=${item.id}&view=1`, backState);

  return (
    <div className="voter-page-shell">
      <div className="tabMain_head voter-page-header">
        <div className="Tab_dropList">
          <h5 style={{ margin: 0 }}>Alphabetical Search</h5>
          <div className="flex_assambly_drop">
            <div>
              <Dropdown
                className="dropdownoforder"
                placeholder="Select Assembly Number"
                options={v.assamblyList || []}
                valueText="value"
                labelText="label"
                isClearable
                selectedOption={v.formOrderName?.assemblyFilter}
                setSelectedOption={v.handleAssemblyChange}
              />
            </div>
            <div>
              <Dropdown
                className="dropdownoforder"
                placeholder="Select Booth Number"
                options={v.boothList || []}
                valueText="value"
                labelText="label"
                isClearable
                selectedOption={v.formOrderName?.boothFilter}
                setSelectedOption={v.handleBoothChange}
              />
            </div>
          </div>
        </div>
      </div>

      {hasSearched && v.filter?.booth_number && (
        <div className="voter-booth-banner">
          <div className="voter-booth-banner-info">
            <FaMapMarkerAlt className="voter-booth-banner-icon" />
            <span className="voter-booth-banner-label">Booth Address :</span>
            <span className="voter-booth-banner-address">{v.boothAddress || "-"}</span>
          </div>
        </div>
      )}

      <div className="anvTask_prog voter-filter-section">
        <div className="voter-filter-bar">
          <div className="voter-filter-item">
            <InputField
              label="Name Starts With"
              labelClass="voter-filter-label"
              placeholder="e.g. A"
              className="anvSearch_input voter-filter-input"
              boxClass="voter-filter-control"
              maxLength={20}
              value={startsWith}
              onChange={(e) => setStartsWith(e.target.value)}
            />
          </div>
          <div className="voter-filter-item voter-filter-toggle-item">
            <button
              type="button"
              className="voter-filter-clear-btn"
              onClick={() => {
                setStartsWith("");
                setHasSearched(false);
                setPage(0);
              }}
            >
              <FaSyncAlt />
              <span>Clear</span>
            </button>
          </div>
          <div className="voter-filter-item voter-filter-action">
            <button
              type="button"
              className="addPlan"
              onClick={() => {
                setPage(0);
                setHasSearched(true);
              }}
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {!hasSearched ? (
        <div className="voter-stats-empty" style={{ margin: 16 }}>
          Select a booth, optionally filter by starting letter, and click Search.
        </div>
      ) : !v.filter?.booth_number ? (
        <div className="voter-stats-empty" style={{ margin: 16 }}>
          Select a booth first - alphabetical search works within one booth at a time.
        </div>
      ) : v.isStatsLoading ? (
        <div className="voter-stats-empty" style={{ margin: 16 }}>
          Loading…
        </div>
      ) : v.statsError ? (
        <div className="voter-stats-error" style={{ margin: 16 }}>
          <span>{v.statsError}</span>
          <button type="button" className="voter-stats-retry-btn" onClick={v.retryStats}>
            <FaSyncAlt />
            <span>Retry</span>
          </button>
        </div>
      ) : (
        <>
          <VoterTable
            voters={pageVoters}
            tableLoading={false}
            userLogin={v.userLogin}
            selectable={false}
            onView={viewVoter}
            onEdit={editVoter}
            onDelete={(item) => v.hendleDeleteTag(item, true)}
          />

          {nameFilteredSorted.length > 0 && (
            <div className="voter-pagination-bar">
              <div className="voter-pagination-info">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, nameFilteredSorted.length)} of{" "}
                {nameFilteredSorted.length} entries
              </div>
              <div className="voter-pagination-controls">
                <div className="voter-per-page">
                  <span>Per Page</span>
                  <Dropdown
                    className="voter-per-page-select"
                    classNamePrefix="voterFilterSelect"
                    options={PER_PAGE_OPTIONS}
                    valueText="value"
                    labelText="label"
                    selectedOption={PER_PAGE_OPTIONS.find((option) => option.value === limit)}
                    setSelectedOption={(data) => {
                      setLimit(data?.value || DEFAULT_PAGE_SIZE);
                      setPage(0);
                    }}
                  />
                </div>
                {pageCount > 1 && (
                  <CustomPagination
                    onPageChange={(data) => setPage(data.selected)}
                    currentPage={page}
                    pageCount={pageCount}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal isOpen={v.openDel} toggle={() => v.setOpenDel(!v.openDel)} onSubmit={v.handleDelete} />
    </div>
  );
};
